import React, { useCallback, useEffect, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  hide,
  offset,
  size as sizing,
  useFloating,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import { Transition } from '@headlessui/react';
import cx from 'classnames';
import moment from 'moment';

import { Calendar, Close, NextSm } from '../../Icons';
import Portal from '../../HOC/Portal';
import InputText from '../InputText';

import type { DateRangePickerValueT } from './DateList';
import Select from './Select';

interface DatePickerPropsT {
  value?: DateRangePickerValueT;
  onChange?: (value: DateRangePickerValueT) => void;
  onClear?: () => void;
  className?: string;
  inputClassName?: string;
  iconClassName?: string;
  popoverClassName?: string;
  placement?: Placement;
  label?: { from: string; to: string };
  placeholder?: { from: string; to: string };
  helperText?: string;
  isError?: boolean;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  mobileTrigger?: React.ReactElement;
  header?: React.ReactElement;
  portalTarget?: Element | DocumentFragment | null;
}

const DateRangePicker: React.FC<DatePickerPropsT> = ({
  value,
  onChange = () => {},
  onClear,
  className,
  inputClassName,
  iconClassName,
  popoverClassName,
  placement,
  label,
  placeholder,
  helperText,
  isError,
  size = 'sm',
  disabled,
  required,
  readOnly,
  mobileTrigger,
  header,
  portalTarget,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [dates, setDates] = useState<DateRangePickerValueT>(value);

  useEffect(() => setDates(value), [value]);

  const { refs, floatingStyles, context } = useFloating({
    placement: placement,
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(1),
      flip({ padding: 10 }),
      sizing({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `min(${rects.reference.width}px, 100vw)`,
            // maxWidth: '630px',
            maxHeight: '600px',
          });
        },
        padding: 5,
      }),
      hide(),
    ],
  });
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown',
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);

  const onDatesChange = useCallback(
    (dates: DateRangePickerValueT) => {
      setDates(dates);
      if (dates?.endDate) {
        onChange(dates);
        setOpen(false);
      }
    },
    [dates, setDates]
  );

  return (
    <>
      <div
        className={cx(
          'inline-flex items-start gap-2 relative w-full md:w-fit max-w-[608px]',
          className && className
        )}
        ref={refs.setReference}
        data-state={open ? 'open' : 'closed'}
        {...getReferenceProps()}
      >
        <InputText
          required={required}
          disabled={disabled}
          isError={isError}
          helperText={helperText}
          size={size}
          inputClassName={cx('date-input', inputClassName && inputClassName)}
          className={cx('flex-1', { 'max-md:hidden': mobileTrigger })}
          label={label?.from}
          placeholder={placeholder?.from ?? 'MM/DD/YYYY'}
          readOnly
          value={
            dates?.startDate ? moment(dates.startDate).format('MM/DD/YYYY') : ''
          }
          // onChange={(e) => {
          //   onSelectDate(new Date(e.currentTarget.value));
          // }}
          rightIcon={
            <Calendar
              className={cx(
                disabled ? 'opacity-50' : 'fill-black cursor-pointer',
                iconClassName && iconClassName
              )}
              width={18}
              height={18}
              onClick={() => {
                if (!readOnly && !disabled) setOpen(true);
              }}
            />
          }
        />
        <div
          className={cx('flex items-center', {
            'h-10': size === 'sm',
            'h-[50px]': size === 'md',
            'h-[60px]': size === 'lg',
            'max-md:hidden': mobileTrigger,
          })}
        >
          <NextSm className="fill-uta-black min-w-fit" width={16} height={16} />
        </div>
        <InputText
          required={required}
          disabled={disabled}
          isError={isError}
          size={size}
          inputClassName={cx('date-input', inputClassName && inputClassName)}
          className={cx('flex-1', { 'max-md:hidden': mobileTrigger })}
          label={label?.to}
          placeholder={placeholder?.to ?? 'MM/DD/YYYY'}
          readOnly
          value={
            dates?.endDate ? moment(dates.endDate).format('MM/DD/YYYY') : ''
          }
          // onChange={(e) => {
          //   onSelectDate(new Date(e.currentTarget.value));
          // }}
          rightIcon={
            <Calendar
              className={cx(
                disabled ? 'opacity-50' : 'fill-black cursor-pointer',
                iconClassName && iconClassName
              )}
              width={18}
              height={18}
              onClick={() => {
                if (!readOnly && !disabled) setOpen(true);
              }}
            />
          }
        />
        {mobileTrigger && (
          <div
            className="md:hidden cursor-pointer"
            onClick={() => setOpen(true)}
          >
            {mobileTrigger}
          </div>
        )}
      </div>

      <Portal portalTarget={portalTarget}>
        {open && (
          <div className="md:hidden fixed inset-0 bg-[#E3E3E3] bg-opacity-75 w-full h-full" />
        )}
        <FloatingFocusManager context={context} modal={false}>
          <Transition
            appear
            show={open}
            as={React.Fragment}
            enter="ease-in-out max-md:duration-500 max-md:transition"
            enterFrom="max-md:!translate-y-full"
            enterTo="max-md:!translate-y-0"
            leave="ease-out max-md:duration-500 max-md:transition"
            leaveFrom="max-md:!translate-y-0"
            leaveTo="opacity-0 max-md:!translate-y-full"
          >
            <div
              className={cx(
                'date-range-popover',
                popoverClassName && popoverClassName,
                {
                  'md:-mt-8': helperText,
                  'md:invisible': context.middlewareData?.hide?.referenceHidden,
                }
              )}
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: 999,
              }}
              {...getFloatingProps()}
            >
              {header && (
                <div className="flex md:hidden items-center justify-center relative px-[15px] py-2.5 border-b border-primary mb-[15px]">
                  {header}
                  <Close
                    onClick={() => setOpen(false)}
                    className="cursor-pointer absolute top-1/2 -translate-y-1/2 right-[15px]"
                  />
                </div>
              )}
              <Select
                value={dates}
                onChange={onDatesChange}
                onClear={onClear}
              />
            </div>
          </Transition>
        </FloatingFocusManager>
      </Portal>
    </>
  );
};

DateRangePicker.defaultProps = {
  placement: 'bottom-start',
};

export default DateRangePicker;
